import React from "react";
import { motion } from "framer-motion";
import { BadgeCheck, ExternalLink } from "lucide-react";

// Typographic certification badges — no external images, rendered in-theme.

const CERTS = [
  {
    issuer: "Google Ads",
    title: "Measurement",
    year: "2024",
    href: "https://skillshop.credential.net/8f7f1236-7bee-43e9-a2b6-63945bd99f7a",
    accent: "#4285F4",
  },
  {
    issuer: "Google Ads",
    title: "Display",
    year: "2024",
    href: "https://skillshop.credential.net/96df759c-7e05-4ce9-ab51-a319f837b122",
    accent: "#34A853",
  },
  {
    issuer: "Google Ads",
    title: "Search",
    year: "2024",
    href: "https://skillshop.credential.net/099f08e8-f465-46de-8a2a-a6c3227da9c9",
    accent: "#FBBC04",
  },
  {
    issuer: "Google Ads",
    title: "AI-Powered Performance",
    year: "2024",
    href: "https://skillshop.credential.net/e082784f-247b-4813-b40a-9d709f6c7658",
    accent: "#EA4335",
  },
  {
    issuer: "HubSpot",
    title: "Marketing Software",
    year: "2024",
    href: "https://app.hubspot.com/academy/achievements/5plt5rkc/en/1/abhinav-dhiman/hubspot-marketing-software",
    accent: "#FF7A59",
  },
  {
    issuer: "HubSpot",
    title: "Digital Advertising",
    year: "2024",
    href: "https://app.hubspot.com/academy/achievements/1zzrl1nl/en/1/abhinav-dhiman/digital-advertising",
    accent: "#FF7A59",
  },
  {
    issuer: "LinkedIn",
    title: "Certified Marketing Insider",
    year: "2024",
    href: "https://verify.skilljar.com/c/jpvmmvjvi5ms",
    accent: "#0A66C2",
  },
];

function BadgeCard({ cert, index, total }) {
  const isLastCol = (index + 1) % 4 === 0;
  const isLastRow = Math.floor(index / 4) === Math.floor((total - 1) / 4);
  return (
    <motion.a
      href={cert.href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`cert-${index}`}
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className={`group relative bg-paper hover:bg-surface transition-colors ${
        !isLastCol ? "md:border-r" : ""
      } ${!isLastRow ? "border-b" : ""} border-line flex flex-col`}
    >
      {/* Vertical accent bar */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1 transition-[width] duration-300 group-hover:w-2"
        style={{ background: cert.accent }}
      />

      <div className="p-6 md:p-8 flex flex-col h-full">
        <div className="flex items-start justify-between mb-8">
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink2">
            {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </span>
          <BadgeCheck
            className="w-5 h-5 text-ink2 group-hover:text-signal transition-colors"
            strokeWidth={1.5}
          />
        </div>

        <div className="font-mono text-[10px] uppercase tracking-[0.22em] mb-2" style={{ color: cert.accent }}>
          {cert.issuer}
        </div>
        <div className="font-heading font-black text-2xl md:text-3xl tracking-tightest leading-[0.95] mb-auto">
          {cert.title}
        </div>

        <div className="mt-10 flex items-center justify-between border-t border-line pt-4">
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink2">
            Certified · {cert.year}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink2 group-hover:text-signal inline-flex items-center gap-1">
            Verify <ExternalLink size={11} />
          </span>
        </div>
      </div>
    </motion.a>
  );
}

export default function Certifications() {
  return (
    <section
      id="certifications"
      data-testid="certifications-section"
      className="py-14 md:py-20 bg-surface"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-center gap-4 mb-8 md:mb-12">
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-signal">§ 03 — Credentials</span>
          <span className="flex-1 h-px bg-line" />
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink2">{CERTS.length} Badges</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-12 items-end">
          <h2 className="md:col-span-8 font-heading font-black text-5xl md:text-7xl leading-[0.9] tracking-tightest">
            Certified by the platforms <span className="text-signal">I live inside</span>.
          </h2>
          <a
            href="https://www.credential.net/profile/abhinav-dhiman/wallet"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="view-all-badges"
            className="md:col-span-4 md:justify-self-end inline-flex items-center gap-2 border border-ink bg-paper hover:bg-ink hover:text-paper px-6 py-4 font-heading font-bold text-[12px] uppercase tracking-[0.2em] transition-colors"
          >
            See Every Badge <ExternalLink size={14} />
          </a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 border border-ink bg-paper">
          {CERTS.map((c, i) => (
            <BadgeCard key={i} cert={c} index={i} total={CERTS.length} />
          ))}

          {/* Filler card to complete the 4-col row */}
          <div className="hidden md:flex flex-col justify-center items-start p-6 md:p-8 bg-ink text-paper">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-paper/60 mb-3">
              + More
            </div>
            <div className="font-heading font-bold text-xl leading-tight tracking-tight">
              Microsoft Advertising · LinkedIn Marketing Solutions · continuously updated.
            </div>
          </div>
        </div>

        <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.22em] text-ink2">
          Verified on Credly · skillshop · HubSpot Academy · LinkedIn
        </p>
      </div>
    </section>
  );
}
